import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import CourseOverview from './CourseOverview';
import CourseComment from './CourseComment';
import CourseRating from './CourseRating';
import { decodeData } from '../../../../utils/encodeAndEncode';
import { getVideoDetailsById, EditVideoContent } from '../../../../services/adminService';
import { usePermissions } from '../../../../context/PermissionsContext';
import { toast } from 'react-toastify';
import SecureVideoPlayer from '../../../../components/common/SecureVideoPlayer';

function VideoPlayer() {
  const { courseId, moduleId, contentId } = useParams();
  const decodedCourseId = decodeData(courseId);
  const decodedModuleId = decodeData(moduleId);
  const decodedContentId = decodeData(contentId);
  const { permissions } = usePermissions();

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [key, setKey] = useState(0);
  const [videoData, setVideoData] = useState(null);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    points: '0',
    newVideoFile: null,
    newVideoPreview: null
  });

  // Upload progress modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('uploading'); // 'uploading', 'success', 'error'
  const [uploadProgress, setUploadProgress] = useState(0);

  // Add validation errors state
  const [errors, setErrors] = useState({
    title: '',
    description: '',
    points: '',
    videoFile: ''
  });
  const [isDraggingUpload, setIsDraggingUpload] = useState(false);

  // Fetch video details
  useEffect(() => {
    const fetchVideoDetails = async () => {
      try {
        const response = await getVideoDetailsById({
          video_id: decodedContentId,
          module_id: decodedModuleId
        });
        // console.log('Video Details Response:', response);

        if (response.success) {
          setVideoData(response.data);
          // Pre-populate edit form with current data
          setEditForm({
            title: response.data.video_title || response.data.video_name || '',
            description: response.data.video_description || '',
            points: response.data.earning_points !== undefined && response.data.earning_points !== null ? response.data.earning_points.toString() : (response.data.earnPoint !== undefined && response.data.earnPoint !== null ? response.data.earnPoint.toString() : '0'),
            newVideoFile: null,
            newVideoPreview: null
          });
        }
      } catch (error) {
        console.error('Error fetching video details:', error);
      }
    };

    if (decodedContentId && decodedModuleId) {
      fetchVideoDetails();
    }
  }, [decodedContentId, decodedModuleId]);

  // Reset when content changes
  useEffect(() => {
    setActiveTab('overview');
    setKey(prevKey => prevKey + 1);
  }, [decodedContentId]);

  const HandleEditVideo = () => {
    if (!permissions.course_module_content_management_edit) {
      toast.warning("You don't have permission to edit video content");
      return;
    }
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    // Reset form to current video data
    setEditForm({
      title: videoData?.video_title || videoData?.video_name || '',
      description: videoData?.video_description || '',
      points: videoData?.earning_points !== undefined && videoData?.earning_points !== null ? videoData?.earning_points.toString() : (videoData?.earnPoint !== undefined && videoData?.earnPoint !== null ? videoData?.earnPoint.toString() : '0'),
      newVideoFile: null,
      newVideoPreview: null
    });
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      title: '',
      description: '',
      points: '',
      videoFile: ''
    };

    // Title validation
    if (!editForm.title.trim()) {
      newErrors.title = 'Video title is required';
      isValid = false;
    } else if (editForm.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
      isValid = false;
    }

    // Description validation
    if (!editForm.description.trim()) {
      newErrors.description = 'Video description is required';
      isValid = false;
    } else if (editForm.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
      isValid = false;
    }

    // Points validation
    if (editForm.points && editForm.points < 0) {
      newErrors.points = 'Points cannot be negative';
      isValid = false;
    }

    // Video file validation
    if (editForm.newVideoFile) {
      const maxSize = 150 * 1024 * 1024; // 150MB
      if (editForm.newVideoFile.size > maxSize) {
        newErrors.videoFile = 'File size must be less than 150MB';
        isValid = false;
      }
      if (!editForm.newVideoFile.type.startsWith('video/')) {
        newErrors.videoFile = 'Please upload a valid video file';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    
    // Check character limits
    if (name === 'title' && value.length > 100) return;
    if (name === 'description' && value.length > 500) return;
    
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const processSelectedVideoFile = (file) => {
    if (!file) return;

    // Clear previous error
    setErrors(prev => ({
      ...prev,
      videoFile: ''
    }));

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setErrors(prev => ({
        ...prev,
        videoFile: 'Please select a valid video file'
      }));
      return;
    }

    // Validate file size (150MB limit)
    const maxSize = 150 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        videoFile: 'File size must be less than 150MB'
      }));
      return;
    }

    setEditForm(prev => ({
      ...prev,
      newVideoFile: file,
      newVideoPreview: URL.createObjectURL(file)
    }));
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    processSelectedVideoFile(file);
  };

  const handleUploadDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingUpload(true);
  };

  const handleUploadDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingUpload(false);
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingUpload(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    processSelectedVideoFile(file);
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Show upload modal
      setShowUploadModal(true);
      setUploadStatus('uploading');
      setUploadProgress(0);

      const formData = new FormData();
      formData.append("video_id", decodedContentId);
      formData.append("video_title", editForm.title);
      formData.append("module_id", decodedModuleId);
      formData.append("description", editForm.description);
      formData.append("earning_points", editForm.points || '0');
      formData.append("duration", videoData?.video_duration || '');

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 1000);

      // Add flags to indicate video status
      const videoChanged = editForm.newVideoFile !== null;

      formData.append("video_changed", videoChanged ? "true" : "false");
      formData.append("video_removed", "false");

      // Only append video file if it has changed
      if (videoChanged && editForm.newVideoFile) {
        formData.append("upload_file", editForm.newVideoFile);
        // console.log("Appending video file:", editForm.newVideoFile.name, "Size:", editForm.newVideoFile.size);
      } else {
        // console.log("No video file to upload, videoChanged:", videoChanged);
      }

      // Debug logging
      // console.log("=== FormData Debug ===");
      for (let pair of formData.entries()) {
        if (pair[1] instanceof File) {
          // console.log(`${pair[0]}:`, `File - ${pair[1].name} (${pair[1].size} bytes, ${pair[1].type})`);
        } else {
          // console.log(`${pair[0]}:`, pair[1]);
        }
      }
      // console.log("=== End FormData Debug ===");

      const response = await EditVideoContent(formData);

      // Clear the progress interval
      clearInterval(progressInterval);

      if (response.success) {
        // Set upload status to success
        setUploadStatus('success');
        setUploadProgress(100);
        
        // Refresh video data
        const videoResponse = await getVideoDetailsById({
          video_id: decodedContentId,
          module_id: decodedModuleId
        });
        if (videoResponse.success) {
          setVideoData(videoResponse.data);
        }
        
        // Close the modal after a short delay
        setTimeout(() => {
          setShowUploadModal(false);
          setShowEditModal(false);
          // Reset form
          setEditForm({
            title: videoResponse.data?.video_title || videoResponse.data?.video_name || '',
            description: videoResponse.data?.video_description || '',
            points: videoResponse.data?.earning_points !== undefined && videoResponse.data?.earning_points !== null ? videoResponse.data?.earning_points.toString() : (videoResponse.data?.earnPoint !== undefined && videoResponse.data?.earnPoint !== null ? videoResponse.data?.earnPoint.toString() : '0'),
            newVideoFile: null,
            newVideoPreview: null
          });
        }, 2000);
      } else {
        setUploadStatus('error');
        setTimeout(() => {
          setShowUploadModal(false);
        }, 2000);
      }
    } catch (error) {
      // console.log(error);
      setUploadStatus('error');
      setTimeout(() => {
        setShowUploadModal(false);
      }, 2000);
    }
  };

  return (
    <>
      <div className="video-section-wrapper px-4 md:px-8 py-5 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <a
              href={`/admin/courses/module/${courseId}/content/${moduleId}`}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 no-underline"
              title="Back to Manage Content"
            >
              <Icon icon="fluent:arrow-left-24-regular" width={18} height={18} />
            </a>
            <div>
              <h4 className="mb-0 text-lg font-semibold text-gray-900">
                {videoData?.video_title || videoData?.video_name || 'Video Player'}
              </h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Manage video playback, comments and ratings for this content.
              </p>
            </div>
          </div>
          {permissions.course_module_content_management_edit && (
            <button 
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={HandleEditVideo}
              disabled={!permissions.course_module_content_management_edit}
              title={!permissions.course_module_content_management_edit ? "You don't have permission to edit video" : "Edit Video"}
            >
              <Icon icon="fluent:edit-24-regular" width={18} height={18} />
              <span>Edit Video</span>
            </button>
          )}
        </div>

        {/* Player */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
          <div className="border-b border-gray-100 px-4 py-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-800">Preview</span>
            {videoData?.video_duration && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-[11px] text-gray-600 border border-gray-200">
                <Icon icon="fluent:timer-24-regular" width={14} height={14} />
                {videoData.video_duration}
              </span>
            )}
          </div>
          <div className="bg-black/5">
            <SecureVideoPlayer
              ref={playerContainerRef}
              videoUrl={videoData?.video_url}
              className="admin-video-player"
              autoplay={false}
              controls={true}
              fluid={true}
              responsive={true}
            />
          </div>
        </div>

        {/* Overview below video */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium">
              <Icon icon="fluent:video-24-regular" width={14} height={14} />
              Video content
            </span>
            {videoData?.is_active !== undefined && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                  videoData.is_active
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200'
                }`}
              >
                {videoData.is_active ? 'Active' : 'Inactive'}
              </span>
            )}
          </div>
          {videoData?.video_description && (
            <p className="text-xs sm:text-sm text-gray-600 leading-snug mb-4">
              {videoData.video_description}
            </p>
          )}
          <CourseOverview key={`overview-${key}`} videoData={videoData} />
        </div>
      </div>

      {/* Edit Video Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                  aria-label="Back"
                >
                  <Icon icon="fluent:arrow-left-24-regular" width={18} height={18} />
                </button>
                <h5 className="text-lg font-semibold mb-0">Edit Video</h5>
              </div>
              <button 
                type="button" 
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCloseEditModal}
                aria-label="Close"
              >
                <Icon icon="mdi:close" width="24" height="24" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {/* Video Title */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-font-primary mb-1.5">Video Title *</label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.title ? 'border-red-500' : 'border-input-border'}`}
                  name="title"
                  value={editForm.title}
                  onChange={handleEditFormChange}
                  placeholder="Enter video title"
                  maxLength={100}
                />
                <div className="flex justify-between mt-1">
                  <small className="text-gray-500">
                    Maximum 100 characters allowed ({100 - editForm.title.length} remaining)
                  </small>
                  {errors.title && (
                    <small className="text-red-500">{errors.title}</small>
                  )}
                </div>
              </div>

              {/* Video Description */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-font-primary mb-1.5">Video Description *</label>
                <textarea
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${errors.description ? 'border-red-500' : 'border-input-border'}`}
                  name="description"
                  value={editForm.description}
                  onChange={handleEditFormChange}
                  placeholder="Enter video description"
                  rows="3"
                  maxLength={500}
                />
                <div className="flex justify-between mt-1">
                  <small className="text-gray-500">
                    Maximum 500 characters allowed ({500 - editForm.description.length} remaining)
                  </small>
                  {errors.description && (
                    <small className="text-red-500">{errors.description}</small>
                  )}
                </div>
              </div>

              {/* Points field hidden in this modal – handled from module content */}

              {/* Upload New Video */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-font-primary mb-1.5">Upload New Video</label>
                <div
                  className={`upload-area relative flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed bg-gray-50 px-4 py-6 text-center cursor-pointer transition hover:bg-gray-100 ${
                    isDraggingUpload ? 'border-primary ring-2 ring-primary/30 bg-indigo-50' : 'border-gray-200'
                  }`}
                  onDragOver={handleUploadDragOver}
                  onDragLeave={handleUploadDragLeave}
                  onDrop={handleUploadDrop}
                  onClick={() => document.getElementById('editVideoUpload')?.click()}
                >
                  <input
                    id="editVideoUpload"
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={handleVideoFileChange}
                  />
                  <div className="flex flex-col items-center justify-center text-center gap-1">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-1">
                      <Icon icon="fluent:video-add-24-regular" width={26} height={26} />
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      Drag &amp; drop video or click to browse
                    </span>
                    <span className="text-xs text-gray-500">
                      MP4, MOV or AVI up to 150MB
                    </span>
                    {editForm.newVideoFile && (
                      <span className="text-xs text-green-600 mt-1">
                        Selected: {editForm.newVideoFile.name}
                      </span>
                    )}
                  </div>
                </div>
                {errors.videoFile && (
                  <div className="text-red-500 text-xs mt-2">{errors.videoFile}</div>
                )}
              </div>

              {/* Current Video Preview */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-font-primary mb-1.5">Current Video</label>
                <div className="border border-gray-200 rounded-lg p-3">
                  {editForm.newVideoPreview ? (
                    <div>
                      <video
                        src={editForm.newVideoPreview}
                        className="w-full"
                        style={{ maxHeight: '200px' }}
                        controls
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                      <div className="mt-2">
                        <small className="text-gray-500 block">
                          File: {editForm.newVideoFile?.name || 'Untitled'}
                        </small>
                        <small className="text-gray-500 block">
                          Size: {(editForm.newVideoFile?.size / (1024 * 1024)).toFixed(2)} MB
                        </small>
                        <small className="text-gray-500 block">
                          Type: {editForm.newVideoFile?.type || 'Unknown'}
                        </small>
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600">New Video Selected</span>
                        </div>
                      </div>
                    </div>
                  ) : videoData?.video_url ? (
                    <div>
                      <video
                        src={videoData.video_url}
                        className="w-full"
                        style={{ maxHeight: '200px' }}
                        controls
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                      <div className="mt-2">
                        <small className="text-gray-500 block">
                          Duration: {videoData.video_duration || 'Unknown'}
                        </small>
                        <small className="text-gray-500 block">
                          File: {videoData.video_title || videoData.video_name || 'Untitled'}
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <Icon icon="fluent:video-24-regular" width="32" height="32" className="text-gray-500" />
                      <p className="text-gray-500 mb-0">No video available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button 
                type="button" 
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                onClick={handleCloseEditModal}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
                onClick={handleSaveChanges}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 text-center">
              {uploadStatus === 'uploading' && (
                <>
                  <div className="mb-3">
                    <Icon icon="fluent:cloud-arrow-up-24-regular" width="48" height="48" className="text-primary" />
                  </div>
                  <h5 className="mb-3">Uploading Video...</h5>
                  <div className="mb-3 bg-gray-200 rounded-full h-5 overflow-hidden">
                    <div 
                      className="bg-primary h-full flex items-center justify-center text-white text-xs font-medium transition-all duration-300 bg-gradient-to-r from-primary via-primary/90 to-primary bg-[length:200%_100%] animate-[shimmer_2s_infinite]"
                      role="progressbar" 
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress} 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                  <p className="text-gray-500 mb-0">Please wait while we process your video...</p>
                </>
              )}

              {uploadStatus === 'success' && (
                <>
                  <div className="mb-3">
                    <Icon icon="fluent:checkmark-circle-24-filled" width="48" height="48" className="text-green-600" />
                  </div>
                  <h5 className="text-green-600 mb-3">Upload Successful!</h5>
                  <p className="text-gray-500 mb-0">Your video has been updated successfully.</p>
                </>
              )}

              {uploadStatus === 'error' && (
                <>
                  <div className="mb-3">
                    <Icon icon="fluent:error-circle-24-filled" width="48" height="48" className="text-red-500" />
                  </div>
                  <h5 className="text-red-500 mb-3">Upload Failed</h5>
                  <p className="text-gray-500 mb-0">There was an error uploading your video. Please try again.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default VideoPlayer;
